import { useEffect, useState } from 'react'

/**
 * Custom hook for debouncing a value
 * Delays updating the returned value until the input value has been stable for the specified delay
 *
 * @template T - The type of the value being debounced
 * @param value - The value to debounce
 * @param delay - The debounce delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * const [searchInput, setSearchInput] = useState('')
 * const debouncedSearch = useDebouncedValue(searchInput, 300)
 *
 * // searchInput updates immediately on keystroke
 * // debouncedSearch only updates 300ms after user stops typing
 */
export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Clean up the timer on value change or component unmount
    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}
